import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Party Scroll v6 — the primary tab nav: Events · Friends · Clubs · +
 *
 * Desktop treatment: a bordered segmented control that lives in a tab page's
 * hero band (replaces the old Events/Guest/Host ModeSwitch). The mobile
 * treatment is the global fixed bottom bar (see MobileTabBar).
 *
 * Active tab derives from the current path unless `active` is passed
 * ('events' | 'people' | 'clubs' | 'calendar' | 'host' | 'start').
 */
// 5-slot nav: Events · Friends · + · Clubs · Calendar (the + sits in the centre).
export const TABS_LEFT = [
  { key: 'events', label: 'Events', path: '/featured' },
  { key: 'people', label: 'Friends', path: '/people' },
];
export const TABS_RIGHT = [
  { key: 'clubs', label: 'Clubs', path: '/clubs' },
  { key: 'calendar', label: 'Calendar', path: '/calendar' },
];
// The handoff nav adds CREATE as a fifth item; the desktop treatment now lives
// in components/chrome/PageBanner (this segmented control is retired in Phase 11).
export const TAB_CREATE = { key: 'start', label: 'Create', path: '/start' };
export const TABS = [...TABS_LEFT, ...TABS_RIGHT];

export function activeTabForPath(pathname) {
  if (pathname.startsWith('/featured') || pathname.startsWith('/events')) return 'events';
  if (pathname.startsWith('/people')) return 'people';
  if (pathname.startsWith('/clubs')) return 'clubs';
  if (pathname.startsWith('/square')) return 'home';
  if (pathname.startsWith('/calendar')) return 'calendar';
  if (pathname.startsWith('/messages')) return 'messages';
  if (pathname === '/host' || pathname.startsWith('/host/')) return 'host';
  if (pathname.startsWith('/start') || pathname.startsWith('/portals') || pathname.startsWith('/signals')) return 'start';
  return null;
}

/**
 * `dense` renders the compact treatment used in the persistent desktop header
 * (Navbar) — smaller padding so the segmented control fits the h-16 top bar.
 * The default (roomy) treatment is used in tab-page hero bands.
 */
export default function PrimaryNav({ active, className = '', dense = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const current = active || activeTabForPath(location.pathname);

  const cellPad = dense ? 'px-4 py-2 text-xs' : 'px-7 py-3 text-sm';
  const plusW = dense ? 'w-10' : 'w-14';
  const plusSize = dense ? 18 : 22;

  const cell = (key, label, path) => {
    const isActive = current === key;
    return (
      <button
        key={key}
        type="button"
        onClick={() => navigate(path)}
        className={`${cellPad} border-r-2 border-black font-mono font-bold uppercase tracking-widest transition-colors ${
          isActive ? 'bg-black text-white' : 'bg-white text-black hover:bg-gray-100'
        }`}
      >
        {label}
      </button>
    );
  };

  return (
    <div className={`inline-flex border-2 border-black bg-white ${className}`}>
      {TABS_LEFT.map((t) => cell(t.key, t.label, t.path))}
      {/* the + — start something (centre slot) */}
      <button
        type="button"
        title="Start something"
        onClick={() => navigate('/start')}
        className={`flex ${plusW} items-center justify-center border-r-2 border-black transition-colors ${
          current === 'start' ? 'bg-black text-white' : 'bg-white text-black hover:bg-gray-100'
        }`}
      >
        <svg width={plusSize} height={plusSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <path d="M5 12h14M12 5v14" />
        </svg>
      </button>
      {TABS_RIGHT.map((t) => cell(t.key, t.label, t.path))}
    </div>
  );
}
